// src/lib/currency.js
// Formats money for any ISO 4217 currency code using the browser's
// built-in Intl.NumberFormat, which already knows the correct symbol,
// symbol placement, and formatting rules for ~180 world currencies. This
// supports whatever currency the geolocation API assigns a business at
// signup (see the OAuth callback workflow's "Resolve Country" node) with
// no per-currency maintenance needed here.
//
// Grouping: every currency uses standard comma-separated thousands
// (1,000,000) via the "en" locale -- EXCEPT INR, which uses "en-IN" so
// Indian merchants (the majority of the current user base) keep seeing
// familiar lakh/crore-style grouping (1,00,000) rather than a regression
// to Western-style grouping.

export function currencySymbol(currency = "INR") {
  try {
    const parts = new Intl.NumberFormat("en", {
      style: "currency",
      currency,
      currencyDisplay: "symbol",
    }).formatToParts(0);
    const symbolPart = parts.find((p) => p.type === "currency");
    return symbolPart ? symbolPart.value : `${currency} `;
  } catch {
    // Invalid/unrecognized ISO code -- fall back to showing the raw code.
    return currency ? `${currency} ` : "";
  }
}

export function formatMoney(amount, currency = "INR") {
  const code = (currency || "INR").toUpperCase();
  const locale = code === "INR" ? "en-IN" : "en";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
      maximumFractionDigits: 0,
    }).format(Number(amount || 0));
  } catch {
    // Invalid/unrecognized ISO code -- fall back to a plain formatted
    // number with the raw currency code as a label, so this never throws
    // and breaks a page even if a bad currency value slips into the data.
    return `${currencySymbol(code)}${Number(amount || 0).toLocaleString(locale, { maximumFractionDigits: 0 })}`;
  }
}

// Kept for backward compatibility with call sites that read a symbol
// directly (e.g. "{CURRENCY_SYMBOLS[currency]}/hour"). Resolves and
// caches symbols lazily on first access via a Proxy, instead of a fixed
// list -- so it still works for any currency, not just a preset few.
const _symbolCache = {};
export const CURRENCY_SYMBOLS = new Proxy(
  {},
  {
    get(_target, currency) {
      if (typeof currency !== "string") return undefined;
      if (!(currency in _symbolCache)) {
        _symbolCache[currency] = currencySymbol(currency);
      }
      return _symbolCache[currency];
    },
  }
);
